/* ==========================================================================
   Custom mega menu  ·  hosewarehouse.com
   Vanilla JS, no dependencies. Progressive enhancement over native
   <details>/<summary>: links work before this runs.

   Desktop : click / Enter / Space toggle a panel; optional hover-to-open;
             Esc closes and returns focus to the trigger; click-away / focus-out
             closes; only one panel open at a time.
   Mobile  : slide-in drawer with focus trap + scroll lock; sub-levels are
             native <details> accordions.
   Editor  : custom-element lifecycle re-inits on shopify:section:load; opens
             the relevant panel on shopify:block:select.
   ========================================================================== */
(function () {
  'use strict';

  if (window.customElements && customElements.get('jd-mega-menu')) return;

  var HOVER_OPEN_DELAY = 90;
  var HOVER_CLOSE_DELAY = 180;

  function focusableWithin(root) {
    var sel = 'a[href], button:not([disabled]), summary, input, select, textarea, [tabindex]:not([tabindex="-1"])';
    return Array.prototype.filter.call(root.querySelectorAll(sel), function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
    });
  }

  class JdMegaMenu extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      var bp = parseInt(this.dataset.breakpoint, 10) || 1024;
      this.hoverEnabled = this.dataset.hover === 'true';
      this.mql = window.matchMedia('(min-width: ' + bp + 'px)');
      this.details = Array.prototype.slice.call(this.querySelectorAll('[data-jd-details]'));
      this.drawer = this.querySelector('[data-jd-drawer]');
      this.drawerToggle = this.querySelector('[data-jd-drawer-toggle]');
      this.hoverTimers = new Map();

      this._onDocPointer = this._onDocPointer.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onFocusout = this._onFocusout.bind(this);
      this._onMedia = this._onMedia.bind(this);
      this._trap = this._trap.bind(this);

      this._bindDetails();
      this._bindRails();
      this._bindDrawer();
      this._bindForms();
      this._reopenFormPane();

      // Take over the theme's own header hamburger: intercept its clicks in
      // the capture phase so empire.js never receives them, and drive our
      // drawer instead. The theme shows that button below 1024px only, which
      // matches this menu's breakpoint.
      this.nativeToggle = this.dataset.nativeToggle === 'true';
      if (this.nativeToggle) {
        this._onNativeToggle = this._onNativeToggle.bind(this);
        document.addEventListener('click', this._onNativeToggle, true);
      }

      document.addEventListener('pointerdown', this._onDocPointer);
      this.addEventListener('keydown', this._onKeydown);
      this.addEventListener('focusout', this._onFocusout);
      this._addMediaListener();

      if (window.Shopify && window.Shopify.designMode) {
        this._onBlockSelect = this._onBlockSelect.bind(this);
        document.addEventListener('shopify:block:select', this._onBlockSelect);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('pointerdown', this._onDocPointer);
      if (this._onNativeToggle) {
        document.removeEventListener('click', this._onNativeToggle, true);
        this._syncNativeButton(false);
      }
      this._removeMediaListener();
      if (this._onBlockSelect) {
        document.removeEventListener('shopify:block:select', this._onBlockSelect);
      }
      this._unlockBody();
    }

    get isDesktop() {
      return this.mql.matches;
    }

    /* ---- desktop panels ------------------------------------------------ */
    _bindDetails() {
      var self = this;
      this.details.forEach(function (d) {
        var summary = d.querySelector('[data-jd-trigger]');

        d.addEventListener('toggle', function () {
          if (summary) summary.setAttribute('aria-expanded', d.open ? 'true' : 'false');
          if (d.open) self._closeOthers(d);
          else self._resetRails(d);
        });

        var item = d.closest('.jd-mega-menu__item') || d.parentElement;
        item.addEventListener('mouseenter', function () {
          if (!self.isDesktop || !self.hoverEnabled) return;
          self._clearTimer(d);
          self._openTimer(d, HOVER_OPEN_DELAY, true);
        });
        item.addEventListener('mouseleave', function () {
          if (!self.isDesktop || !self.hoverEnabled) return;
          self._clearTimer(d);
          self._openTimer(d, HOVER_CLOSE_DELAY, false);
        });
      });
    }

    /* ---- drill-down rails ---------------------------------------------- */
    // Rail links live inside the element, so listeners are GC'd with it —
    // nothing to clean up in disconnectedCallback.
    _bindRails() {
      var self = this;

      // Remember each rail's server-rendered default so a closed panel can be
      // reset — reopening should never show the pane a past hover left behind.
      this._railDefaults = new Map();
      Array.prototype.forEach.call(this.querySelectorAll('.jd-mega-menu__rail-layout'), function (layout) {
        self._railDefaults.set(layout, layout.querySelector('[data-jd-rail-link][aria-current="true"]'));
      });

      Array.prototype.forEach.call(this.querySelectorAll('[data-jd-rail-link]'), function (a) {
        a.addEventListener('mouseenter', function () {
          if (self._railTimer) window.clearTimeout(self._railTimer);
          self._railTimer = window.setTimeout(function () { self._activateRail(a); }, HOVER_OPEN_DELAY);
        });
        a.addEventListener('mouseleave', function () {
          if (self._railTimer) window.clearTimeout(self._railTimer);
        });
        a.addEventListener('focus', function () { self._activateRail(a); });
      });

      // Plain rail links (no pane of their own): hovering or focusing one
      // clears the pane a previously hovered item left displayed. Same intent
      // delay, so passing over one en route to the pane doesn't blank it.
      Array.prototype.forEach.call(
        this.querySelectorAll('.jd-mega-menu__rail-link:not([data-jd-rail-link])'),
        function (a) {
          a.addEventListener('mouseenter', function () {
            if (self._railTimer) window.clearTimeout(self._railTimer);
            self._railTimer = window.setTimeout(function () { self._clearRail(a); }, HOVER_OPEN_DELAY);
          });
          a.addEventListener('mouseleave', function () {
            if (self._railTimer) window.clearTimeout(self._railTimer);
          });
          a.addEventListener('focus', function () { self._clearRail(a); });
        }
      );
    }

    // Strip every is-current/aria-current the JS has set within one rail
    // layout. Plain links' server-rendered aria-current="page" is untouched
    // (the selector only matches paned links).
    _clearRailState(layout) {
      Array.prototype.forEach.call(layout.querySelectorAll('.is-current'), function (el) {
        el.classList.remove('is-current');
      });
      Array.prototype.forEach.call(layout.querySelectorAll('[data-jd-rail-link][aria-current]'), function (el) {
        el.removeAttribute('aria-current');
      });
    }

    _clearRail(a) {
      var layout = a.closest('.jd-mega-menu__rail-layout');
      if (layout) this._clearRailState(layout);
    }

    // A closing panel forgets its hover state: each rail goes back to its
    // server-rendered default pane, so the next open starts fresh.
    _resetRails(d) {
      var self = this;
      if (this._railTimer) window.clearTimeout(this._railTimer);
      Array.prototype.forEach.call(d.querySelectorAll('.jd-mega-menu__rail-layout'), function (layout) {
        var def = self._railDefaults && self._railDefaults.get(layout);
        if (def) {
          self._activateRail(def);
        } else {
          self._clearRailState(layout);
        }
      });
    }

    _activateRail(a) {
      var layout = a.closest('.jd-mega-menu__rail-layout');
      var pane = document.getElementById(a.getAttribute('data-pane'));
      if (!layout || !pane) return;

      this._clearRailState(layout);

      var item = a.closest('.jd-mega-menu__rail-item');
      if (item) item.classList.add('is-current');
      a.setAttribute('aria-current', 'true');
      pane.classList.add('is-current');
    }

    /* ---- pane contact forms -------------------------------------------- */
    // The form is a plain Shopify POST; sessionStorage only remembers which
    // pane owned it so the round-trip result can be shown. try/catch keeps
    // private-browsing modes harmless — submission never depends on this.
    _bindForms() {
      Array.prototype.forEach.call(this.querySelectorAll('.jd-mega-menu__form'), function (f) {
        f.addEventListener('submit', function () {
          var pane = f.closest('[data-jd-pane]');
          if (!pane) return;
          try {
            window.sessionStorage.setItem('jd:contact-pane', pane.id);
          } catch (err) {
            /* no auto-reopen, nothing else lost */
          }
        });
      });
    }

    // After Shopify's contact POST round-trip (success redirect with
    // ?contact_posted=true, or an error re-render), reopen the pane that owns
    // the form so the banner is seen. The flag is cleared unconditionally so
    // it can never fire stale — and it never fires for the contact PAGE's own
    // form (which sets no flag) even though form.posted_successfully? is
    // global to every contact form on the page.
    _reopenFormPane() {
      var paneId = null;
      try {
        paneId = window.sessionStorage.getItem('jd:contact-pane');
        if (paneId) window.sessionStorage.removeItem('jd:contact-pane');
      } catch (err) {
        return;
      }
      if (!paneId || !this.isDesktop) return;
      var pane = document.getElementById(paneId);
      if (!pane || !this.contains(pane)) return;
      if (!pane.querySelector('.jd-mega-menu__form-message')) return;
      var d = pane.closest('[data-jd-details]');
      // Opening fires the existing toggle listener: aria sync + close others.
      if (d) d.open = true;
      var railLink = this.querySelector('[data-jd-rail-link][data-pane="' + paneId + '"]');
      if (railLink) this._activateRail(railLink);
    }

    _openTimer(d, delay, open) {
      var self = this;
      var t = window.setTimeout(function () {
        d.open = open;
      }, delay);
      this.hoverTimers.set(d, t);
    }
    _clearTimer(d) {
      var t = this.hoverTimers.get(d);
      if (t) {
        window.clearTimeout(t);
        this.hoverTimers.delete(d);
      }
    }
    _closeOthers(except) {
      this.details.forEach(function (d) {
        if (d !== except && d.open) d.open = false;
      });
    }
    _closeAll() {
      this.details.forEach(function (d) {
        if (d.open) d.open = false;
      });
    }
    _openDetail() {
      return this.details.filter(function (d) { return d.open; })[0];
    }

    _onDocPointer(e) {
      if (!this.isDesktop) return;
      if (this.contains(e.target)) return;
      this._closeAll();
    }

    _onFocusout(e) {
      if (!this.isDesktop) return;
      if (e.relatedTarget && this.contains(e.relatedTarget)) return;
      this._closeAll();
    }

    _onKeydown(e) {
      if (e.key !== 'Escape' && e.key !== 'Esc') return;
      if (this.drawer && this.drawer.classList.contains('is-open')) {
        this._closeDrawer();
        return;
      }
      var open = this._openDetail();
      if (open) {
        var summary = open.querySelector('[data-jd-trigger]');
        open.open = false;
        if (summary) summary.focus();
      }
    }

    /* ---- mobile drawer ------------------------------------------------- */
    _bindDrawer() {
      var self = this;
      if (this.drawerToggle) {
        this.drawerToggle.addEventListener('click', function () { self._openDrawer(); });
      }
      Array.prototype.forEach.call(this.querySelectorAll('[data-jd-drawer-close]'), function (el) {
        el.addEventListener('click', function () { self._closeDrawer(); });
      });
    }

    _onNativeToggle(e) {
      if (this.isDesktop) return;
      var btn = e.target && e.target.closest ? e.target.closest('.site-header-menu-toggle') : null;
      if (!btn) return;
      e.preventDefault();
      e.stopImmediatePropagation(); // theme's own drawer never opens
      if (this.drawer && this.drawer.classList.contains('is-open')) this._closeDrawer();
      else this._openDrawer();
    }

    // Mirror open state onto the theme's button: .active runs Empire's own
    // hamburger→X animation, all CSS.
    _syncNativeButton(open) {
      if (!this.nativeToggle) return;
      var btn = document.querySelector('.site-header-menu-toggle');
      if (!btn) return;
      btn.classList.toggle('active', open);
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    // The theme header (logo/search bar) is painted above our drawer, so the
    // drawer must start below it or its head + first items hide behind it.
    // Measure ONLY the visible top bar `[data-site-header-main]` — the outer
    // `[data-site-header]` also wraps the tall nav dropdown, which would make
    // the offset huge and push the panel off-screen. Measured at open-time so
    // it tracks the bar's height + sticky/scrolled state (body is scroll-locked
    // while open, so the value stays valid).
    _drawerTopOffset() {
      var el = document.querySelector('[data-site-header-main]');
      if (!el) return 0;
      var bottom = Math.round(el.getBoundingClientRect().bottom);
      if (bottom < 0) bottom = 0;
      // Safety: never eat more than half the viewport (guards against ever
      // measuring a tall wrapper and hiding the whole panel).
      var cap = Math.round((window.innerHeight || 0) * 0.5);
      if (cap && bottom > cap) bottom = 0;
      return bottom;
    }

    _openDrawer() {
      if (!this.drawer) return;
      this._lastFocused = document.activeElement;
      this.drawer.style.setProperty('--jd-drawer-top', this._drawerTopOffset() + 'px');
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      if (this.drawerToggle) this.drawerToggle.setAttribute('aria-expanded', 'true');
      this._syncNativeButton(true);
      this._lockBody();
      this.drawer.addEventListener('keydown', this._trap);
      var f = focusableWithin(this.drawer);
      if (f.length) f[0].focus();
    }

    _closeDrawer() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      if (this.drawerToggle) this.drawerToggle.setAttribute('aria-expanded', 'false');
      this._syncNativeButton(false);
      this.drawer.removeEventListener('keydown', this._trap);
      this._unlockBody();
      if (this._lastFocused && typeof this._lastFocused.focus === 'function') {
        this._lastFocused.focus();
      }
    }

    _trap(e) {
      if (e.key !== 'Tab') return;
      var f = focusableWithin(this.drawer);
      if (!f.length) return;
      var first = f[0];
      var last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    _lockBody() { document.documentElement.classList.add('jd-scroll-lock'); }
    _unlockBody() { document.documentElement.classList.remove('jd-scroll-lock'); }

    /* ---- media + editor ------------------------------------------------ */
    _addMediaListener() {
      if (this.mql.addEventListener) this.mql.addEventListener('change', this._onMedia);
      else if (this.mql.addListener) this.mql.addListener(this._onMedia);
    }
    _removeMediaListener() {
      if (this.mql.removeEventListener) this.mql.removeEventListener('change', this._onMedia);
      else if (this.mql.removeListener) this.mql.removeListener(this._onMedia);
    }
    _onMedia() {
      this._closeAll();
      if (this.isDesktop && this.drawer && this.drawer.classList.contains('is-open')) {
        this._closeDrawer();
      }
    }

    _onBlockSelect(e) {
      if (!this.contains(e.target)) return;
      var d = e.target.closest('[data-jd-details]');
      if (d) {
        this._closeOthers(d);
        d.open = true;
      }
      // category_tiles block: also switch its pane into view
      var pane = e.target.closest('[data-jd-pane]');
      if (pane && pane.id) {
        var railLink = this.querySelector('[data-jd-rail-link][data-pane="' + pane.id + '"]');
        if (railLink) this._activateRail(railLink);
      }
    }
  };

  customElements.define('jd-mega-menu', JdMegaMenu);
})();
